<script lang="ts">
  import { currentTree, setTreeVisibility } from '../lib/stores';
  import { hasCloud, db } from '../lib/db';
  import { logAudit } from '../lib/audit';

  export let open = false;

  let mode: 'read' | 'live' | 'fork' = 'read';
  let email = '';
  let busy = false;
  let result = '';

  async function share() {
    if (!$currentTree || !email) return;
    busy = true; result = '';
    try {
      if (hasCloud) {
        const realm = $currentTree.realmId ?? `rlm-${$currentTree.id}`;
        if (!$currentTree.realmId) {
          await db.realms.add({ realmId: realm, name: $currentTree.title });
          await db.trees.update($currentTree.id, { realmId: realm });
          await db.people.where('treeId').equals($currentTree.id).modify({ realmId: realm });
          await db.relationships.where('treeId').equals($currentTree.id).modify({ realmId: realm });
        }
        await db.members.add({
          realmId: realm, email,
          roles: mode === 'read' ? ['read'] : ['read', 'write'],
          permissions: { manage: false },
          invite: true,
        } as Record<string, unknown>);
        result = `invite sent · ${email}`;
      } else {
        result = `local mode — share simulated. in production, creates a Dexie Cloud realm invite for ${email}.`;
      }
      await logAudit('tree.share', { treeId: $currentTree.id, meta: { email, mode } });
    } catch (e) {
      result = (e as Error).message;
    } finally { busy = false; }
  }

  async function togglePublic() {
    if (!$currentTree) return;
    const next = $currentTree.visibility === 'public' ? 'private' : 'public';
    if (next === 'public') {
      const ok = confirm(
        'Publishing adds DECEASED ancestors to the public corpus. ' +
        'Living people remain redacted unless explicit consent is recorded. ' +
        'Continue?',
      );
      if (!ok) return;
    }
    await setTreeVisibility($currentTree.id, next);
  }
</script>

{#if open && $currentTree}
  <div class="backdrop"
       on:click={() => (open = false)}
       on:keydown={(e) => e.key === 'Escape' && (open = false)}
       role="button"
       tabindex="-1">
    <div class="sheet card" role="dialog" aria-label="Share tree"
         on:click|stopPropagation
         on:keydown|stopPropagation>
      <header>
        <div class="stack-tight">
          <h3>Share "{$currentTree.title}"</h3>
          <span class="chip">tree.{$currentTree.id.slice(0, 8)}</span>
        </div>
        <button class="ghost small" on:click={() => (open = false)} aria-label="Close">✕</button>
      </header>

      <div class="stack">
        <fieldset class="mode">
          <legend class="label-like">access mode</legend>
          <label class="choice" class:active={mode === 'read'}>
            <input type="radio" bind:group={mode} value="read" />
            <span><strong>read-only.</strong> collaborator can view, not edit.</span>
          </label>
          <label class="choice" class:active={mode === 'live'}>
            <input type="radio" bind:group={mode} value="live" />
            <span><strong>live edit.</strong> edits flow back to the owner. realm-synced.</span>
          </label>
          <label class="choice" class:active={mode === 'fork'}>
            <input type="radio" bind:group={mode} value="fork" />
            <span><strong>fork.</strong> collaborator takes a branch, can propose merging later.</span>
          </label>
        </fieldset>

        <div class="field">
          <label for="inv">invite email</label>
          <input id="inv" type="email" bind:value={email} placeholder="cousin@family.name" />
        </div>

        <div class="row">
          <button class="primary" on:click={share} disabled={busy || !email}>
            {busy ? 'sending…' : 'send invite →'}
          </button>
          <button on:click={() => (open = false)}>cancel</button>
        </div>
        {#if result}<div class="pill {result.startsWith('invite sent') ? 'ok' : 'warn'}">{result}</div>{/if}

        <hr class="rule" />

        <div>
          <div class="row head-row">
            <strong>Public corpus</strong>
            <span class="chip {$currentTree.visibility === 'public' ? 'gold' : ''}">
              {$currentTree.visibility}
            </span>
          </div>
          <p class="dim small">
            Publishing contributes DECEASED ancestors to the shared public tree so
            other users can find and merge with them. Living people never appear
            without explicit consent.
          </p>
          <button class={$currentTree.visibility === 'public' ? '' : 'gold'} on:click={togglePublic}>
            {$currentTree.visibility === 'public' ? 'unpublish' : 'publish to public corpus'}
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .backdrop {
    position: fixed; inset: 0;
    background: rgba(0,0,0,.6);
    display: grid; place-items: center; z-index: 100; padding: 16px;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
  }
  .sheet { width: 520px; max-width: 100%; }
  header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 14px; gap: 10px; }
  .stack-tight { display: flex; flex-direction: column; gap: 6px; }
  h3 { margin: 0; font-size: var(--ls-step-3); }
  .small { font-size: var(--ls-step-0); }
  .head-row { justify-content: space-between; margin-bottom: 4px; }

  fieldset.mode { border: 1px solid var(--ls-border); border-radius: var(--ls-radius); padding: 6px 4px; margin: 0; }
  .label-like {
    font-family: var(--ls-font-mono); font-size: 11px;
    color: var(--ls-text-dim); padding: 0 8px;
    letter-spacing: .02em;
  }
  .choice {
    display: flex; gap: 10px; align-items: flex-start;
    padding: 8px 10px; border-radius: var(--ls-radius);
    cursor: pointer;
    color: var(--ls-text-muted);
    font-size: var(--ls-step-0);
  }
  .choice:hover { background: var(--ls-surface-2); }
  .choice.active { background: color-mix(in srgb, var(--ls-signal) 10%, transparent); color: var(--ls-text); }
  .choice input { width: auto; accent-color: var(--ls-signal); margin-top: 3px; }
  .choice strong { color: var(--ls-ink); font-weight: 600; }
</style>
